import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

export interface LineaCanasta {
  entryNo: number;
  vendorCode: string;
  vendorName: string | null;
  /** Monto a pagar en la moneda original de la factura. */
  montoAPagar: number;
  /** Monto a pagar convertido a colones — solo lectura, calculado con la tasa implícita de la factura. */
  montoAPagarLCY: number;
  currencyCode: string | null;
  /** RemainingAmtLCY / RemainingAmount de la factura original — para recalcular montoAPagarLCY al editar montoAPagar. */
  tasaImplicita: number;
  /** Saldo pendiente original de la factura — tope para montoAPagar. */
  montoMaximo: number;
  cuentaBancoOrigen: string | null;
}

interface PropuestaEnConstruccionValue {
  lineas: LineaCanasta[];
  estaSeleccionada: (entryNo: number) => boolean;
  agregar: (linea: LineaCanasta) => void;
  agregarVarias: (lineas: LineaCanasta[]) => void;
  quitar: (entryNo: number) => void;
  alternar: (linea: LineaCanasta) => void;
  actualizarMonto: (entryNo: number, montoAPagar: number) => void;
  actualizarCuenta: (entryNo: number, cuentaBancoOrigen: string | null) => void;
  reemplazar: (lineas: LineaCanasta[]) => void;
  limpiar: () => void;
  totalPorMoneda: { currencyCode: string; total: number; totalLCY: number }[];
}

const CLAVE_STORAGE = 'adelante-pagos:propuesta-en-construccion';

const PropuestaEnConstruccionContext = createContext<PropuestaEnConstruccionValue | null>(null);

function leerDeStorage(): LineaCanasta[] {
  try {
    const crudo = localStorage.getItem(CLAVE_STORAGE);
    if (!crudo) return [];
    const parseado = JSON.parse(crudo);
    return Array.isArray(parseado) ? parseado : [];
  } catch {
    return [];
  }
}

export function PropuestaEnConstruccionProvider({ children }: { children: ReactNode }) {
  const [lineas, setLineas] = useState<LineaCanasta[]>(() => leerDeStorage());

  useEffect(() => {
    try {
      localStorage.setItem(CLAVE_STORAGE, JSON.stringify(lineas));
    } catch {
      // localStorage no disponible (navegación privada, etc.) — se pierde al recargar, sin romper la app.
    }
  }, [lineas]);

  const estaSeleccionada = useCallback(
    (entryNo: number) => lineas.some((l) => l.entryNo === entryNo),
    [lineas],
  );

  const agregar = useCallback((linea: LineaCanasta) => {
    setLineas((actual) =>
      actual.some((l) => l.entryNo === linea.entryNo) ? actual : [...actual, linea],
    );
  }, []);

  const agregarVarias = useCallback((nuevas: LineaCanasta[]) => {
    setLineas((actual) => {
      const existentes = new Set(actual.map((l) => l.entryNo));
      return [...actual, ...nuevas.filter((l) => !existentes.has(l.entryNo))];
    });
  }, []);

  const quitar = useCallback((entryNo: number) => {
    setLineas((actual) => actual.filter((l) => l.entryNo !== entryNo));
  }, []);

  const alternar = useCallback((linea: LineaCanasta) => {
    setLineas((actual) =>
      actual.some((l) => l.entryNo === linea.entryNo)
        ? actual.filter((l) => l.entryNo !== linea.entryNo)
        : [...actual, linea],
    );
  }, []);

  const actualizarMonto = useCallback((entryNo: number, montoAPagar: number) => {
    setLineas((actual) =>
      actual.map((l) =>
        l.entryNo === entryNo
          ? { ...l, montoAPagar, montoAPagarLCY: montoAPagar * l.tasaImplicita }
          : l,
      ),
    );
  }, []);

  const actualizarCuenta = useCallback((entryNo: number, cuentaBancoOrigen: string | null) => {
    setLineas((actual) =>
      actual.map((l) => (l.entryNo === entryNo ? { ...l, cuentaBancoOrigen } : l)),
    );
  }, []);

  const reemplazar = useCallback((nuevas: LineaCanasta[]) => setLineas(nuevas), []);

  const limpiar = useCallback(() => setLineas([]), []);

  const totalPorMoneda = useMemo(() => {
    const mapa = new Map<string, { total: number; totalLCY: number }>();
    for (const l of lineas) {
      const moneda = l.currencyCode || 'CRC';
      const actual = mapa.get(moneda) ?? { total: 0, totalLCY: 0 };
      mapa.set(moneda, {
        total: actual.total + l.montoAPagar,
        totalLCY: actual.totalLCY + l.montoAPagarLCY,
      });
    }
    return [...mapa.entries()].map(([currencyCode, v]) => ({ currencyCode, ...v }));
  }, [lineas]);

  const value = useMemo(
    () => ({
      lineas,
      estaSeleccionada,
      agregar,
      agregarVarias,
      quitar,
      alternar,
      actualizarMonto,
      actualizarCuenta,
      reemplazar,
      limpiar,
      totalPorMoneda,
    }),
    [
      lineas,
      estaSeleccionada,
      agregar,
      agregarVarias,
      quitar,
      alternar,
      actualizarMonto,
      actualizarCuenta,
      reemplazar,
      limpiar,
      totalPorMoneda,
    ],
  );

  return (
    <PropuestaEnConstruccionContext.Provider value={value}>
      {children}
    </PropuestaEnConstruccionContext.Provider>
  );
}

export function usePropuestaEnConstruccion(): PropuestaEnConstruccionValue {
  const ctx = useContext(PropuestaEnConstruccionContext);
  if (!ctx) {
    throw new Error(
      'usePropuestaEnConstruccion debe usarse dentro de <PropuestaEnConstruccionProvider>',
    );
  }
  return ctx;
}
